/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import * as React from 'react';
import { css } from '@emotion/react';
import { Tooltip } from '@hotjar/webapp/components/Tooltip';
import { Checkbox } from '@hotjar/webapp/components/Checkbox';

import { useTranslation, Trans } from 'react-i18next';
import type { Site } from '@hotjar/site';
import type { Control } from 'react-hook-form';
import { useForm, Controller } from 'react-hook-form';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { tokens, Button } from '@hotjar/hotsauce';
import type { TFunction } from 'i18next';
import { bus, refreshData } from '@hotjar/webapp/utils/events';
import { useHelpHotjarUrl } from '@hotjar/translations';

import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { Section, Paragraph, Link, Separator } from '../styles';
import { siteApi } from '../api';

const SUPPRESSION_LEARN_MORE_LINK = '/articles/115012439167';
const ALLOWED_INPUT_FIELDS_LEARN_MORE_LINK = '/articles/115015563287';

interface DataSuppressionProps {
  site: Site;
  onFormDirty: (isDirty: boolean) => void;
  onFormSaved: () => void;
}

type FormResult = {
  suppress_all: Site['suppressAll'];
  suppress_location: Site['suppressLocation'];
  suppress_text: Site['suppressText'];
  anonymize_digits: Site['anonymizeDigits'];
  anonymize_emails: Site['anonymizeEmails'];
  recording_capture_keystrokes: Site['recordingCaptureKeystrokes'];
};

// FIXME: Please fix this. You may refer to CFE-266 for more details.
// eslint-disable-next-line func-params-args/func-params
const getControlledCheckbox = (
  control: Control<FormResult>,
  name: keyof FormResult,
  isDisabled: boolean,
  t: TFunction,
  helpHotjarUrl: string,
  onHelpClicked?: () => void,
) => {
  // Trans node for allowed input fields description
  const description: string | React.ReactNode =
    name === 'recording_capture_keystrokes' ? (
      <Trans i18nKey="common:site-settings.data-suppression.recording_capture_keystrokes-description">
        By default, Hotjar suppresses all keystroke data, except for
        <Link
          // needed as Checkbox has its own style for label links that we do not want here
          css={css`
            color: #666 !important;
          `}
          rel="noreferrer"
          href={`${helpHotjarUrl}${ALLOWED_INPUT_FIELDS_LEARN_MORE_LINK}`}
          target="_blank"
          onClick={onHelpClicked}
        >
          allowed input fields
        </Link>
        . Activate this setting if you wish to stop sending and storing data from input fields you
        allowed.
      </Trans>
    ) : (
      t(`common:site-settings.data-suppression.${name}-description`)
    );

  return (
    <Controller
      control={control}
      name={name}
      render={({ onChange, value }) => (
        <Checkbox
          partial={false}
          disabled={isDisabled}
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          labelCss={css`
            color: ${tokens.color.text_default};
            margin-bottom: ${tokens.spacer.x3};
          `}
          description={description}
        >
          {t(`common:site-settings.data-suppression.${name}`)}
        </Checkbox>
      )}
    />
  );
};

export const DataSuppression = ({
  site,
  onFormDirty,
  onFormSaved,
}: DataSuppressionProps): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const { click } = useTrack();
  const { success, error } = useNotify();
  const { control, watch, handleSubmit, reset, formState, setValue } = useForm<FormResult>({
    mode: 'onBlur',
    defaultValues: {
      suppress_all: site.suppressAll,
      suppress_location: site.suppressLocation,
      suppress_text: site.suppressText,
      anonymize_digits: site.anonymizeDigits,
      anonymize_emails: site.anonymizeEmails,
      recording_capture_keystrokes: !site.recordingCaptureKeystrokes,
    },
  });

  const { hasPermission } = useAuthorization();
  const hasSitesUpdatePermission = hasPermission('site', `${site.id}`, 'sites-update');

  const { isDirty, isSubmitting } = formState;
  const [canSubmit, setCanSubmit] = useState(true);

  // watcher on fields value
  const watchFields = watch();

  const trackClick = (elementName: string) => {
    click(elementName, 'site.settings_suppression', {
      account_id: site.account.id,
      organization_id: site.organization.id,
      site_id: site.id,
      state_event_was_triggered_on: 'site.settings',
      url_event_was_triggered_on: `/site/${site.id}/settings#suppression`,
    });
  };

  useEffect(() => {
    // on load and when site changes, we set values to the ones from the site.
    reset({
      suppress_all: site.suppressAll,
      suppress_location: site.suppressLocation,
      suppress_text: site.suppressText,
      anonymize_digits: site.anonymizeDigits,
      anonymize_emails: site.anonymizeEmails,
      recording_capture_keystrokes: !site.recordingCaptureKeystrokes,
    });
    setCanSubmit(true);
  }, [site, reset]);

  useEffect(() => {
    onFormDirty(isDirty);
  }, [isDirty, onFormDirty]);

  useEffect(() => {
    if (watchFields.suppress_all) {
      setValue('suppress_text', true);
    }
  }, [setValue, watchFields.suppress_all]);

  useEffect(() => {
    if (watchFields.suppress_text) {
      setValue('anonymize_digits', true);
      setValue('anonymize_emails', true);
      setValue('recording_capture_keystrokes', true);
    }
  }, [setValue, watchFields.suppress_text]);

  const onSuccess = () => {
    onFormSaved();
    success(t('common:site-settings.data-suppression.save-success'));
  };

  const onSubmit = async (data: FormResult) => {
    setCanSubmit(false);
    const response = await siteApi.put(site.account.id as number, site.id, 'suppression', {
      ...data,
      recording_capture_keystrokes: !data.recording_capture_keystrokes,
    });

    if (response.success) {
      const hasRefreshDataListener = bus.emitter.listeners(refreshData.eventType).length > 0;

      if (hasRefreshDataListener) {
        bus.publish(refreshData(() => onSuccess()));
      } else {
        onSuccess();
      }
    } else {
      setCanSubmit(true);
      error(t('common:site-settings.data-suppression.save-error'));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Section>
        <Paragraph>
          <Trans i18nKey="common:site-settings.data-suppression.suppression-information">
            When suppressing data, Hotjar will neither send nor store specific types of information.
            Instead, this information is replaced with placeholders or asterisks (‘***’) in your
            Recordings, Heatmaps, and Feedback screenshots.
            <Link
              href={`${helpHotjarUrl}${SUPPRESSION_LEARN_MORE_LINK}`}
              target="_blank"
              onClick={() => {
                trackClick('site.settings_suppression-learn-more-link');
              }}
              rel="noreferrer"
            >
              Learn more
            </Link>
            .
          </Trans>
        </Paragraph>

        <Separator />

        {getControlledCheckbox(
          control,
          'suppress_location',
          !hasSitesUpdatePermission,
          t,
          helpHotjarUrl,
        )}
        {getControlledCheckbox(
          control,
          'suppress_all',
          !hasSitesUpdatePermission,
          t,
          helpHotjarUrl,
        )}
        {getControlledCheckbox(
          control,
          'suppress_text',
          !hasSitesUpdatePermission || watchFields.suppress_all,
          t,
          helpHotjarUrl,
        )}
        {getControlledCheckbox(
          control,
          'anonymize_digits',
          !hasSitesUpdatePermission || watchFields.suppress_text,
          t,
          helpHotjarUrl,
        )}
        {getControlledCheckbox(
          control,
          'anonymize_emails',
          !hasSitesUpdatePermission || watchFields.suppress_text,
          t,
          helpHotjarUrl,
        )}
        {getControlledCheckbox(
          control,
          'recording_capture_keystrokes',
          !hasSitesUpdatePermission || watchFields.suppress_text,
          t,
          helpHotjarUrl,
          () => {
            trackClick('site.settings_suppression-allow-keystroke-data-help-link');
          },
        )}

        <Tooltip
          enabled={!hasSitesUpdatePermission}
          content={t('common:site-settings.no-edit-access') as string}
          placement="right"
        >
          <div
            css={css`
              display: inline-block;
            `}
          >
            <Button
              type="submit"
              isDisabled={!hasSitesUpdatePermission || isSubmitting || !isDirty || !canSubmit}
              isLoading={!canSubmit}
              loadingText={t('common:site-settings.saving-changes')}
            >
              {t('common:site-settings.save-changes')}
            </Button>
          </div>
        </Tooltip>
      </Section>
    </form>
  );
};
